import { useMemo } from "react";
import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountTree,
  Cloud,
  Memory,
  Storage,
  ViewInAr,
} from "@mui/icons-material";
import { useFetchContext } from "../../../utils/fetch";
import { useAuthHeaders } from "../../../utils/query/query";
import AwsLogo from "../../../images/svg/chip/aws-logo.svg";
import AzureLogo from "../../../images/svg/chip/azure-logo.svg";
import GcpLogo from "../../../images/svg/chip/gcp-logo.svg";
import {
  CardIcon,
  EMPTY,
  InfraCard,
  InfrastructureInfo,
  fetchInfrastructure,
  toCards,
} from "./infrastructureApi";

/**
 * The cloud resources behind a cluster, on the ported
 * GET /api/cluster/{name}/infrastructure endpoint.
 *
 * Laid out as V2's Infrastructure tab is: a card per resource, and within a card a wrapping grid
 * of value-over-label pairs rather than a two-column table. Support engineers already know that
 * shape and scan it for one identifier at a time, so the value is the prominent half and the label
 * sits underneath in small grey text.
 *
 * Redis and Postgres share a row where there is room, again matching V2, since those two are the
 * pair most often read against each other.
 */
export default function InfrastructureTab({
  clusterName,
  organizationId,
  orkesAccount,
}: {
  clusterName?: string;
  organizationId?: string;
  orkesAccount?: boolean;
}) {
  const context = useFetchContext();
  // fetchWithContext attaches no auth headers of its own; a direct caller must pass them.
  const authHeaders = useAuthHeaders();

  const query = useQuery<InfrastructureInfo>(
    ["cluster-infrastructure", clusterName, organizationId],
    () =>
      fetchInfrastructure(
        context,
        authHeaders,
        clusterName as string,
        organizationId
      ),
    { enabled: Boolean(clusterName) }
  );

  const cards = useMemo(
    () =>
      query.data ? toCards(query.data, { organizationId, orkesAccount }) : [],
    [query.data, organizationId, orkesAccount]
  );

  const providerLogo = (provider?: string) => {
    switch ((provider ?? "").toUpperCase()) {
      case "AZURE":
        return AzureLogo;
      case "GCP":
        return GcpLogo;
      case "AWS":
        return AwsLogo;
      default:
        return undefined;
    }
  };

  const icon = (kind: CardIcon, provider?: string) => {
    const sx = { fontSize: 20, color: "text.secondary" };
    if (kind === "provider") {
      const logo = providerLogo(provider);
      return logo ? (
        <Box component="img" src={logo} alt="" sx={{ width: 20, height: 20 }} />
      ) : (
        <Cloud sx={sx} />
      );
    }
    if (kind === "redis") return <Memory sx={sx} />;
    if (kind === "postgres") return <Storage sx={sx} />;
    if (kind === "network") return <AccountTree sx={sx} />;
    return <ViewInAr sx={sx} />;
  };

  const renderCard = (card: InfraCard) => (
    <Card key={card.key} variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {icon(card.icon, query.data?.cloudProvider)}
          <Typography variant="subtitle1" fontWeight={600}>
            {card.title}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            rowGap: 2,
            columnGap: 3,
          }}
        >
          {card.fields.map((field) => (
            <Box key={field.label} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  wordBreak: "break-all",
                  // A dash marks a value we hold nothing for, so de-emphasise it and let the real
                  // identifiers carry the card.
                  color:
                    field.value === EMPTY ? "text.disabled" : "text.primary",
                }}
              >
                {field.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {field.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const datastores = cards.filter(
    (c) => c.key === "redis" || c.key === "postgres"
  );
  const computing = cards.filter((c) => c.key === "computing");
  const rest = cards.filter(
    (c) => !["redis", "postgres", "computing"].includes(c.key)
  );

  return (
    <Box sx={{ p: 2 }}>
      {query.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load infrastructure for this cluster.
        </Alert>
      )}

      {query.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : !query.data ? null : (
        <Stack spacing={2}>
          {computing.map(renderCard)}
          {datastores.length > 0 && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="stretch"
            >
              {datastores.map(renderCard)}
            </Stack>
          )}
          {rest.map(renderCard)}
        </Stack>
      )}
    </Box>
  );
}
